import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CartContext } from "../../contexts/CartContext";
import { AuthContext } from "../../contexts/AuthContext";
import { ToastContext } from "../../contexts/ToastContext";
import { apiFetch } from "../../services/api";
import { useApi } from "../../hooks/useApi";
import { normalizeCategories } from "../../utils/categoryHelpers";
import { requireLogin } from "../../utils/authHelpers";
import { getHeroImages } from "../../utils/heroImages";
import {
  LOCAL_CAROUSEL_IMAGES,
} from "../../constants/images";
import KitItemCard from "../../components/KitItemCard";

export default function ShopPage() {
  const navigate = useNavigate();
  const [urlParams, setUrlParams] = useSearchParams();
  const initialCategory = urlParams.get("cat") || urlParams.get("brand") || "";
  const initialSearch = urlParams.get("q") || "";

  const { count, setOpen, addItem } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const toast = useContext(ToastContext);
  const [category, setCategory] = useState(initialCategory || "");
  useEffect(() => {
  setCategory(initialCategory || "");
}, [initialCategory]);

useEffect(() => {
  console.log("Current category:", category);
}, [category]);

  const [sort] = useState("name_asc");
  const [page, setPageNum] = useState(1);
  const [searchInput, setSearchInput] = useState(initialSearch || "");
  const [search, setSearch] = useState(initialSearch || "");
  useEffect(() => {
    setSearch(initialSearch || "");
    setSearchInput(initialSearch || "");
  }, [initialSearch]);

  useEffect(() => {
    const next = new URLSearchParams(urlParams);
    if (category) next.set("cat", category);
    else next.delete("cat");
    next.delete("brand");
    if (search) next.set("q", search);
    else next.delete("q");
    if (next.toString() !== urlParams.toString()) {
      setUrlParams(next, { replace: true });
    }
  }, [category, search]);
  const [aiQuery, setAiQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [currentImage, setCurrentImage] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);
  const [kitItems, setKitItems] = useState([]);
  const [kitLoading, setKitLoading] = useState(false);
  const [showVisualizer, setShowVisualizer] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [carouselPaused, setCarouselPaused] = useState(false);
  

 const getProductImage = async (productId) => {
  if (!productId) return "/images/product-placeholder.png";

  try {
    const res = await apiFetch(`/images/product/${productId}`);
    const data = await res.json();

    return data?.url || "/images/product-placeholder.png";
  } catch (err) {
    console.error("Image load failed:", err);
    return "/images/product-placeholder.png";
  }
};

const getCategoryImage = (categoryId) => {
  if (!categoryId) return "./assets/category-placeholder.png";
  return `/images/category/${categoryId}`;
};

const [selectedSet, setSelectedSet] = useState(null);
const [sets, setSets] = useState([]);

const loadSetsByCategory = async (categoryId) => {
  try {
    const res = await apiFetch(
      categoryId ? `/kits/category/${categoryId}` : `/kits`
    );
    const data = await res.json().catch(() => null);
    const list = res.ok && Array.isArray(data) ? data : [];

    setSets(list);
    setSelectedSet(list.length > 0 ? list[0] : null);
  } catch (err) {
    console.error("Failed to load kits:", err);
    setSets([]);
    setSelectedSet(null);
  }
};

const heroRef = useRef(null);
  const params = new URLSearchParams({ page: String(page), limit: "12", sort });
  if (category) params.set("category", category);
  if (search) params.set("search", search);

  const { data: productsData, loading } = useApi(
    "/products?" + params.toString(),
    [category, sort, page, search],
  );
  const { data: categoriesData } = useApi("/categories");
  const categoriesList = normalizeCategories(categoriesData);

  useEffect(() => {
    const categoryId = category
      ? categoriesList.find((c) => c.slug === category)?.id
      : null;
    loadSetsByCategory(categoryId || null);
  }, [category, categoriesData]);

  const aiParams = new URLSearchParams({ q: aiQuery, limit: "24" });
  if (category) aiParams.set("category", category);
  const { data: aiData, loading: aiLoading } = useApi(
    aiQuery ? "/products/ai-search?" + aiParams.toString() : null,
    [aiQuery, category],
  );

  const collection = useMemo(() => {
    if (aiQuery) return aiData?.data || [];
    return productsData?.data || [];
  }, [aiQuery, aiData, productsData]);

  const total = aiQuery
    ? aiData?.total || collection.length
    : productsData?.total || 0;
  const totalPages = Math.max(1, Math.ceil((productsData?.total || 0) / 12));
  const selectedProduct =
  collection.find((p) => p.id === selectedId) || null;

  const selectedCategoryName = category
    ? (categoriesList || []).find((c) => c.slug === category)?.name ||
      "Selected Category"
    : "All Categories";



//   // const imageFor = (product) => {
//   //   if (!product) return "";
//   //   // Local demo mode: always show the curated full look image as main image.
//   //   return LOCAL_MAIN_IMAGE;
//   // };

//   // const gridImageFor = (index) => {
//   //   if (!LOCAL_PRODUCT_GRID_IMAGES.length) return "";
//   //   return LOCAL_PRODUCT_GRID_IMAGES[index % LOCAL_PRODUCT_GRID_IMAGES.length];
//   // };

  const [heroImages, setHeroImages] = useState(() => getHeroImages(category));
  const [currentHeroImagesIndex, setCurrentHeroImagesIndex] = useState(0);

  // garden kit roles
  const ROLE_ORDER = [
  "Soil & Compost",
  "Seeds & Plants",
  "Fertiliser",
  "Tools",
  "Pest Control",
];
const loadKitItems = async (setId) => {
  if (!setId) return;

  setKitLoading(true);

  try {
    const res = await apiFetch(`/kits/${setId}`);
    const data = await res.json();

    const products = data?.products || [];

    const kit = ROLE_ORDER.map((role) => {
      const product = products.find((p) => p.role === role);

      return product
        ? { ...product, role, placeholder: false }
        : {
            id: `placeholder-${role}`,
            role,
            name: "Product Coming Soon",
            description: "This kit piece has not been added yet.",
            price: null,
            stock: 0,
            image_url: null,
            placeholder: true,
          };
    });

    setKitItems(kit);
  } catch (err) {
    console.error(err);
    setKitItems([]);
  } finally {
    setKitLoading(false);
  }
};

  useEffect(() => {
    const imgs = getHeroImages(category);
    setHeroImages(imgs && imgs.length ? imgs : LOCAL_CAROUSEL_IMAGES.slice());
    setCurrentHeroImagesIndex(0);
  }, [category]);

 useEffect(() => {
  if (carouselPaused || !heroImages?.length) return;

  const interval = setInterval(() => {
    setCurrentHeroImagesIndex((prev) =>
      prev >= heroImages.length - 1 ? 0 : prev + 1
    );
  }, 4000);

  return () => clearInterval(interval);
}, [heroImages, carouselPaused]);

console.log("Selected product:", selectedProduct);
console.log("Product images:", selectedProduct?.images);
const [productImages, setProductImages] = useState({});
useEffect(() => {
  const loadImages = async () => {
    const imageMap = {};

    for (const p of collection) {
      try {
        const res = await apiFetch(`/images/product/${p.id}`);
        const data = await res.json();
        imageMap[p.id] = data?.url;
      } catch {
        imageMap[p.id] = "/images/product-placeholder.png";
      }
    }

    setProductImages(imageMap);
  };

  if (collection.length) loadImages();
}, [collection]);

const hasSelectedImage = productImages.length > 0;

  const money = (value) => `£${Number(value || 0).toFixed(2)}`;

  useEffect(() => {
    if (!collection.length) {
      setSelectedId(null);
      return;
    }
    const exists = collection.some((p) => p.id === selectedId);
    if (!exists) setSelectedId(collection[0].id);
  }, [collection, selectedId]);

console.log("Selected Product:", selectedProduct);
console.log("Selected Product Set ID:", selectedProduct?.set_id);

  // Fetch kit items when product changes
useEffect(() => {
  if (!selectedSet?.id) return;
  loadKitItems(selectedSet.id);
}, [selectedSet?.id]);

  const runAiSearch = (query) => {
    const next = String(query || "").trim();
    if (next.length < 2) {
      toast("Enter at least 2 characters for search", "error");
      return;
    }
    if (next.length > 80) {
      toast("Search must be 80 characters or less", "error");
      return;
    }
    setChatOpen(true);
    setAiQuery(next);
    setSearch("");
    setSearchInput(next);
    setPageNum(1);
  };

  const findSimilar = () => {
    if (!selectedProduct?.category_slug) return;
    setCategory(selectedProduct.category_slug);
    setAiQuery("");
    setPageNum(1);
    toast(
      `Showing similar items in ${selectedProduct.category_name || "this category"}`,
    );
  };

  // Shop The Look carousel helpers (Sets section only)
  const currentSetIndex = useMemo(() => {
    const idx = sets.findIndex((s) => s.id === selectedSet?.id);
    return idx >= 0 ? idx : 0;
  }, [sets, selectedSet]);

  const goToSet = (nextIndex) => {
    if (!sets.length) return;
    const clamped = ((nextIndex % sets.length) + sets.length) % sets.length;
    setSelectedSet(sets[clamped]);
  };

  const addLookToBag = () => {
    if (!requireLogin(user, navigate, toast, "Please sign in to add items to your shopping bag.")) {
      return;
    }
    kitItems
      .filter((item) => !item.placeholder && item.id)
      .forEach((item) => addItem(item.id, 1));
  };

  return (
    <div className="luxe-shop-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap');
        .luxe-shop-root {
          --black: #F8F6F2;
          --white: #111110;
          --cream: #3D3D3B;
          --gold: #C49A3C;
          --gold-light: #A27A2F;
          --soft: #888780;
          --hero-anim-ms: 720ms;
          --hero-anim-blur: 3px;
          --hero-anim-scale: 1.018;
          --hero-anim-ease: cubic-bezier(.18,.68,.2,1);
          font-family: "DM Sans", "Segoe UI", sans-serif;
          color: var(--white);
          min-height: 100vh;
          background: FFFFFF;
          padding: 10px 10px 72px;
          position: relative;
          overflow-x: hidden;
        }
        .luxe-noise {
          position: fixed;
          inset: 0;
          pointer-events: none;
          z-index: 0;
          opacity: .08;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='220' height='220' viewBox='0 0 220 220'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='220' height='220' filter='url(%23n)' opacity='0.35'/%3E%3C/svg%3E");
        }
        .luxe-shell {
          max-width: 1320px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
          animation: luxeFadeUp .5s ease;
        }
        @keyframes luxeFadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .luxe-icon-btn {
          border: 1px solid rgba(0,0,0,0.06);
          border-radius: 999px;
          color: #3D3D3B;
          background: #FFFFFF;
          padding: 7px 10px;
          font-size: 12px;
          cursor: pointer;
        }
        .luxe-hero {
          margin-top: 8px;
          border: 1px solid rgba(0,0,0,0.06);
          border-radius: 14px;
          padding: 8px 10px;
          color: #888780;
          letter-spacing: .08em;
          text-transform: uppercase;
          font-size: 10px;
          text-align: center;
          background: linear-gradient(90deg, rgba(196,154,60,.12), rgba(255,255,255,.95), rgba(196,154,60,.12));
        }
        .luxe-main {
          margin-top: 10px;
          display: grid;
          grid-template-columns: 1fr;
          gap: 10px;
        }
        .luxe-btn {
          border: none;
          background:  #C49A3C;
          color: #fff;
          border-radius: 10px;
          font-weight: 700;
          padding: 10px 14px;
          cursor: pointer;
          transition: transform .15s ease, box-shadow .2s ease;
        }
        .luxe-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 8px 22px rgba(196, 154, 60, 0.28);
        }
        .luxe-card {
  background: #FFFFFF;
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 28px;
  overflow: hidden;
  box-shadow:
    0 10px 30px rgba(0,0,0,.05),
    0 30px 80px rgba(0,0,0,.04);
}
      .luxe-viewer {
  position: relative;
  overflow: hidden;
  min-height: clamp(440px, 68vh, 760px);

  background: #FFFFFF;
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 28px;

  box-shadow:
    0 12px 32px rgba(0,0,0,.05),
    0 4px 12px rgba(0,0,0,.03);

  padding: 24px;
}

          .luxe-hero-image {
  width: 100%;
  height: clamp(440px, 68vh, 760px);

  object-fit: contain;

  background:
    radial-gradient(circle at top,
    rgba(196,154,60,.08),
    transparent 60%),
    #FAFAF8;

  border-radius: 20px;

  animation: fadeLuxury .7s ease;
  transition: transform .4s ease;
}
  .luxe-viewer:hover .luxe-hero-image {
  transform: scale(1.01);
}
.luxe-skeleton {
  position: relative;
  overflow: hidden;
  background: #eceae4;
}

.luxe-skeleton::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255,255,255,.75),
    transparent
  );
  animation: skeleton-loading 1.2s infinite;
}

@keyframes skeleton-loading {
  100% {
    transform: translateX(100%);
  }
}
          @keyframes fadeLuxury {
            from {
              opacity: 0;
              transform: scale(1.02);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
                
                  .luxe-viewer img {
            width: 100%;
            height: clamp(440px, 68vh, 760px);
            object-fit: contain;
            background: #f5f5f5;
          }
        .luxe-hero-image {
          animation: luxeCinematicIn var(--hero-anim-ms) var(--hero-anim-ease);
          will-change: transform, opacity, filter;
        }
        @keyframes luxeCinematicIn {
          0% {
            opacity: 0;
            filter: blur(var(--hero-anim-blur)) saturate(.92);
            transform: perspective(900px) rotateY(calc(var(--angle) * 1deg)) scale(var(--hero-anim-scale));
          }
          100% {
            opacity: 1;
            filter: blur(0) saturate(1);
            transform: perspective(900px) rotateY(calc(var(--angle) * 1deg)) scale(1);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .luxe-viewer img,
          .luxe-hero-image,
          .luxe-item,
          .luxe-btn,
         .luxe-overlay button {
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 999px;
  background: rgba(255,255,255,.95);
  backdrop-filter: blur(12px);
  color: #111110;
  padding: 12px 22px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all .25s ease;
}
        }
        .luxe-badge {
          position: absolute;
          top: 10px;
          left: 10px;
          border: 1px solid rgba(181,138,43,.35);
          border-radius: 999px;
          padding: 5px 9px;
          font-size: 10px;
          letter-spacing: .12em;
          text-transform: uppercase;
          background: rgba(255,255,255,.9);
          color: #A27A2F;
        }
        .luxe-overlay {
          position: absolute;
          left: 10px;
          right: 10px;
          bottom: 10px;
          display: flex;
          justify-content: space-between;
          gap: 6px;
          flex-wrap: wrap;
        }
        .luxe-overlay button {
          border: none;

          

          background: rgba(255,255,255,.95);

          backdrop-filter: blur(10px);

          color: #111110;

          padding: 10px 18px;

          font-size: 12px;

          font-weight: 700;

          cursor: pointer;

          transition: .25s;
        }

      .luxe-overlay button:hover {
  transform: translateY(-2px);

  background: #FCFAF5;

  border-color: rgba(196,154,60,.4);

  box-shadow:
    0 10px 24px rgba(196,154,60,.12);
}
    .luxe-primary-action {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 10px;

  padding: 14px 28px;
  border: 1px solid rgba(255,255,255,.08);
 

  background:
    linear-gradient(
      135deg,
      rgba(32,32,35,.98),
      rgba(18,18,20,.98)
    );

  color: #fff;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: .3px;

  cursor: pointer;
  overflow: hidden;

  box-shadow:
    0 8px 30px rgba(0,0,0,.25),
    0 0 0 1px rgba(255,255,255,.03),
    0 0 35px rgba(109,87,255,.18);

  transition:
    transform .25s ease,
    box-shadow .25s ease,
    border-color .25s ease;
}

.luxe-primary-action::before {
  content: "";
  position: absolute;
  inset: 0;

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255,255,255,.08),
      transparent
    );

  transform: translateX(-100%);
  transition: transform .8s ease;
}

.luxe-primary-action:hover {
  transform: translateY(-3px);

  border-color: rgba(138,99,255,.4);

  box-shadow:
    0 15px 40px rgba(0,0,0,.35),
    0 0 50px rgba(109,87,255,.35);
}

.luxe-primary-action:hover::before {
  transform: translateX(100%);
}

.luxe-primary-action:active {
  transform: translateY(0);
}

.luxe-primary-action-icon {
  font-size: 17px;
  color: #A78BFA;
}

  .luxe-secondary-btn {
  background: #FFFFFF;
  border: 1px solid rgba(0,0,0,0.06);
  color: #111110;
  padding: 10px 18px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all .25s ease;
}

.luxe-secondary-btn:hover {
  border-color: #C49A3C;
  background: #FCFAF5;
  transform: translateY(-1px);
}
      .luxe-meta {
  padding: 36px;
  background: #FFFFFF;
  border-top: 1px solid rgba(0,0,0,0.06);
  display: flex;
  flex-direction: column;
  gap: 14px;
}
       .luxe-kicker {
  color: #C49A3C;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .18em;
}
       .luxe-title {
  margin: 0;
  font-family: "Playfair Display", serif;
   font-size: clamp(24px, 2.4vw, 34px);
  line-height: 1.15;
  font-weight: 600;
  color: #111110;
}
      .luxe-sub {
  color: #6B6B67;
  font-size: 15px;
  line-height: 1.8;
  max-width: 700px;
}
        // .luxe-collection {
        //   display: grid;
        //   grid-template-rows: auto auto;
        //   min-height: 0;
        // }
        .luxe-collection-head {
          border-bottom: 1px solid rgba(0,0,0,0.06);
          padding: 10px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
       .luxe-items {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
            gap: 20px;
            padding: 24px;
          }
          .luxe-item {
            background: #fff;
            border: 1px solid rgba(0,0,0,0.06);
            border-radius: 20px;
            padding: 18px;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
            transition: all .3s ease;
            cursor: pointer;
            width: 100%;
          }

          .luxe-item:hover {
            transform: translateY(-6px);
            box-shadow: 0 18px 40px rgba(0,0,0,.05);
            border-color: #C49A3C;
          }

          .luxe-item.active {
            background: #FCFAF5;
            border-color: rgba(196,154,60,.45);
          }
          .luxe-item img {
            width: 100%;
            height: 260px;
            object-fit: cover;
            border-radius: 14px;
          }
        .luxe-chat {
          position: fixed;
          right: 16px;
          bottom: 14px;
          z-index: 40;
          width: min(380px, calc(100vw - 20px));
          box-shadow: 0 12px 28px rgba(0,0,0,.06);
          border-radius: 14px;
        }
        .luxe-chat-toggle {
          width: 100%;
          border: 1px solid rgba(0,0,0,0.06);
          border-radius: 14px;
          background: #ffffff;
          color: #21414d;
          padding: 9px 11px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
        }
        .luxe-chat-panel {
          margin-top: 6px;
          border: 1px solid rgba(0,0,0,0.06);
          border-radius: 14px;
          background: #ffffff;
          padding: 10px;
          display: none;
        }
        .luxe-chat.open .luxe-chat-panel { display: block; }
        .luxe-chat-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-bottom: 8px;
        }
        .luxe-chat-chips button {
          border: 1px solid rgba(181,138,43,.36);
          border-radius: 999px;
          background: rgba(181,138,43,.08);
          color: #805f17;
          padding: 6px 10px;
          font-size: 11px;
          cursor: pointer;
        }
        .luxe-chat-input-row {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 6px;
        }
        .luxe-chat-input-row input {
          border: 1px solid rgba(0,0,0,0.05);
          border-radius: 10px;
          background: #FBFAF8;
          color: var(--white);
          padding: 8px 10px;
        }
        .luxe-fit-modal {
          position: fixed;
          inset: 0;
          background: rgba(20,31,35,.24);
          display: none;
          align-items: center;
          justify-content: center;
          z-index: 60;
          padding: 16px;
        }
        .luxe-fit-modal.open { display: flex; }
        .luxe-fit-content {
          width: min(560px, 100%);
          border-radius: 14px;
          border: 1px solid rgba(0,0,0,0.06);
          background: #fff;
          padding: 14px;
          color: #1f2a2e;
        }
        @media (max-width: 900px) {
          .luxe-shop-root {
            --hero-anim-ms: 460ms;
            --hero-anim-blur: 1.2px;
            --hero-anim-scale: 1.01;
          }
          .luxe-shop-root { padding: 10px 8px 120px; }
          .luxe-icon-btn { min-height: 42px; padding: 9px 12px; font-size: 13px; }
          .luxe-main { grid-template-columns: 1fr; }
          .luxe-viewer,
          .luxe-viewer img { height: min(68vh, 560px); min-height: min(68vh, 560px); }
          .luxe-overlay { left: 10px; right: 10px; bottom: 10px; }
          .luxe-overlay button { min-height: 44px; padding: 10px 13px; font-size: 13px; }
          .luxe-collection { min-height: 0; }
          .luxe-items { padding-bottom: 10px; gap: 8px; }
          .luxe-item { flex-basis: 84vw; }
          .luxe-chat {
            left: 10px;
            right: 10px;
            width: auto;
            bottom: calc(10px + env(safe-area-inset-bottom));
          }
          .luxe-chat-toggle { min-height: 48px; padding: 12px; }
          .luxe-chat-input-row { grid-template-columns: 1fr; }
          .luxe-chat-input-row .luxe-btn { width: 100%; }
        }
        @media (min-width: 901px) {
          .luxe-collection {
            position: sticky;
            bottom: 10px;
            z-index: 8;
            box-shadow: 0 10px 28px rgba(0,0,0,.05);
          }
        }
      `}</style>

      <div className="luxe-noise" />
      <div className="luxe-shell">
        <div className="luxe-main">
          {/* <section className="luxe-card"> */}
            <div 
            ref={heroRef}
            className="luxe-viewer">
              {/* Hero image is now category-driven and independent from product state */}
              <img
                // src={
                //   (heroImages && heroImages.length && heroImages[currentHeroImagesIndex]) ||
                //   LOCAL_CAROUSEL_IMAGES[0]
                // }
                src={selectedSet?.image_url }
                alt={selectedSet?.name || selectedCategoryName}
                className="luxe-hero-image"
                onError={(e) => {
                  console.error("Hero image failed:", e.currentTarget.src);
                  // ensure fallback if image fails to load
                  e.currentTarget.src = LOCAL_CAROUSEL_IMAGES[0];
                }}
              />
              {/* <div className="luxe-badge">{angle}° View</div> */}
              <div className="luxe-overlay">
                <div style={{ display: "flex", gap: 8 }}>
  <button
    onClick={() =>
      setCurrentHeroImagesIndex((prev) =>
        prev === 0 ? Math.max(0, heroImages.length - 1) : prev - 1
      )
    }
  >
    ◀ Prev
  </button>

  <button
    onClick={() =>
      setCurrentHeroImagesIndex((prev) =>
        prev >= (heroImages.length - 1) ? 0 : prev + 1
      )
    }
  >
    Next ▶
  </button>
  <button
  className="luxe-icon-btn"
  onClick={() => setCarouselPaused((prev) => !prev)}
>
  {carouselPaused ? "▶ Resume" : "⏸ Pause"}
</button>

  <button onClick={findSimilar}>
    Find Similar
  </button>
</div>
 <button
  className="luxe-primary-action"
  onClick={async () => {
    if (!requireLogin(user, navigate, toast, "Please sign in to add items to your shopping bag.")) {
      return;
    }
    const pid = selectedProduct?.id || selectedId;

    if (!pid) {
      console.warn(
        "Virtual Fit-On: no selected product to add",
        selectedProduct,
        selectedId
      );
      if (typeof setOpen === "function") setOpen(true);
      return;
    }

    const belongsToSet =
      !!(selectedProduct?.set_id || selectedSet?.id) &&
      kitItems.some((item) => !item.placeholder && item.id);

    if (belongsToSet) {
      console.log("Virtual Fit-On: adding kit set to cart", kitItems);

      for (const item of kitItems) {
        if (item.placeholder) continue;
        try {
          await addItem(item.id, 1);
          console.log("Virtual Fit-On: addItem succeeded", item.id);
        } catch (err) {
          console.error("Virtual Fit-On: addItem failed", item.id, err);
        }
      }

      if (typeof setOpen === "function") setOpen(true);
      return;
    }

    console.log("Virtual Fit-On: adding product to cart", pid);

    try {
      await addItem(pid, 1);
      console.log("Virtual Fit-On: addItem succeeded", pid);
    } catch (err) {
      console.error("Virtual Fit-On: addItem failed", err);
    }

    if (typeof setOpen === "function") setOpen(true);
  }}
>
  <span className="luxe-primary-action-icon">✦</span>
  Virtual Fit-On
</button>
              </div>
            </div>

  <div className="luxe-meta"
   style={{
    background: "#FFFFFF",
    borderRadius: 28,
    padding: "42px",
    border: "1px solid rgba(0,0,0,0.06)",
    boxShadow: "0 18px 50px rgba(0,0,0,.06)",
  }}>

  {/* NAME + PRICE */}
  <div
    style={{
      display: "flex",
      alignItems: "baseline",
      gap: 14,
      flexWrap: "wrap",
    }}
  >
    <h1
      style={{
         fontFamily: "Playfair Display, serif",
      fontSize: 35,
      color: "#111110",
      margin: 0,
      }}
    >
      {selectedSet?.name || "No Set Selected"}
    </h1>

    <span
      style={{
        color: "#C49A3C",
        fontWeight: 700,
        fontSize: 22,
        letterSpacing: "-0.02em",
      }}
    >
      ·{" "}
      £{selectedSet?.price || "0.00"}
    </span>
  </div>

  {/* GOLD LINE */}
  <div
    style={{
      width: 80,
      height: 2,
      background: "#C49A3C",
      marginTop: 8,
      marginBottom: 10,
    }}
  />

  {/* CATEGORY */}
  <div
    style={{
      color: "#888780",
      fontSize: 13,
      textTransform: "uppercase",
      letterSpacing: ".12em",
      fontWeight: 600,
      marginBottom: 18,
    }}
  >
    {selectedSet?.category_name}
  </div>

  {/* ACTION BUTTONS */}
  {/* <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexWrap: "wrap",
    }}
  >
   

    <button
  onClick={() => setAiQuery("")}
  className="luxe-secondary-btn"
>
  Show Normal Search
</button>
  </div> */}

</div>

              {/* Kit Breakdown Section */}
              {selectedSet && (
                <div
                  style={{
                    marginTop: 10,
                    paddingTop: 40,
                    borderTop: "1px solid rgba(0,0,0,0.06)",
                  }}
                >
                 <div
  style={{
    textAlign: "center",
    marginBottom: 50,
  }}
>
  <div
    style={{
      color: "#C49A3C",
      textTransform: "uppercase",
      letterSpacing: ".15em",
      fontWeight: 700,
      fontSize: 12,
      marginBottom: 12,
    }}
  >
    Styled For You
  </div>

  <h3
    style={{
      fontFamily: "Playfair Display, serif",
      fontSize: 42,
      color: "#111110",
      margin: 0,
    }}
  >
    Complete The Look
  </h3>

  <p
    style={{
      color: "#888780",
      fontSize: 16,
      maxWidth: 650,
      margin: "16px auto 0",
      lineHeight: 1.7,
    }}
  >
    Carefully selected pieces designed to perfectly complement
    your chosen style.
  </p>

  <div
    style={{
      width: 70,
      height: 2,
      background: "#C49A3C",
      margin: "24px auto 0",
    }}
  />
</div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(240px, 1fr))",
                      gap: 20,
                    }}
                  >
                    {kitLoading ? (
                      <div
                        style={{
                          gridColumn: "1/-1",
                          textAlign: "center",
                          color: "#888780",
                          padding: "30px 0",
                        }}
                      >
                        Loading kit items...
                      </div>
                    ) : kitItems.length === 0 ? (
                      <div
                        style={{
                          gridColumn: "1/-1",
                          textAlign: "center",
                          color: "#888780",
                          padding: "20px",
                          border: "1px dashed rgba(0,0,0,0.06)",
                          borderRadius: 12,
                          background: "#FAFAF8",
                        }}
                      >
                        No kit items available for this look yet.
                      </div>
                    ) : (
                        kitItems.map((item) => (
                        <KitItemCard
                          key={item.id}
                          item={item}
                          onView={(slug) => navigate("/product/" + slug)}
                        />
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          
          {/* </section> */}


{/* SETS SECTION - Shop The Look */}
<div className="stl-section">
  <style>{`
    .stl-section {
      margin-top: 40px;
      background: #fff;
      border: 1px solid rgba(0,0,0,0.06);
      border-radius: 20px;
      overflow: hidden;
    }
    .stl-header {
      padding: 40px 32px 32px;
      text-align: center;
      background: #fff;
      border-bottom: 1px solid rgba(0,0,0,0.06);
    }
    .stl-eyebrow {
      color: #C49A3C;
      text-transform: uppercase;
      letter-spacing: .15em;
      font-weight: 700;
      font-size: 12px;
      margin-bottom: 12px;
    }
    .stl-title {
      margin: 0;
      font-family: "Playfair Display", serif;
      font-size: 38px;
      font-weight: 600;
      color: #111110;
    }
    .stl-collection-row {
      display: flex;
      align-items: baseline;
      justify-content: center;
      gap: 12px;
      flex-wrap: wrap;
      margin-top: 18px;
    }
    .stl-collection-name {
      font-family: "Playfair Display", serif;
      font-size: 22px;
      font-style: italic;
      color: #111110;
    }
    .stl-collection-category {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 999px;
      background: #F8F4EA;
      color: #C49A3C;
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: .12em;
    }
    .stl-collection-price {
      color: #C49A3C;
      font-weight: 700;
      font-size: 18px;
    }
    .stl-dots {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      margin-top: 22px;
    }
    .stl-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #ECE7DF;
      border: none;
      padding: 0;
      cursor: pointer;
      transition: all .3s ease;
    }
    .stl-dot.active {
      background: #C49A3C;
      width: 24px;
      border-radius: 4px;
    }
    .stl-body {
      padding: 48px 32px;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 48px;
      align-items: start;
    }
    .stl-image-wrap {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      border-radius: 20px;
      background: #F8F8F6;
      box-shadow: 0 20px 50px rgba(0,0,0,.05);
      min-height: clamp(360px, 42vw, 620px);
      max-height: 720px;
    }
    .stl-image {
      width: 100%;
      height: 100%;
      max-height: 720px;
      object-fit: contain;
      display: block;
      margin: 0 auto;
    }
    .stl-arrow {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: 44px;
      height: 44px;
      border-radius: 50%;
      border: 1px solid rgba(0,0,0,0.06);
      background: rgba(255,255,255,.95);
      color: #111110;
      font-size: 16px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all .25s ease;
    }
    .stl-arrow:hover {
      background: #111110;
      color: #fff;
      border-color: #111110;
    }
    .stl-arrow.prev { left: 16px; }
    .stl-arrow.next { right: 16px; }
    .stl-products {
      display: flex;
      flex-direction: column;
    }
    .stl-product-row {
      display: flex;
      align-items: center;
      gap: 20px;
      padding: 22px 0;
      border-bottom: 1px solid rgba(0,0,0,0.06);
      transition: transform .25s ease;
    }
    .stl-product-row:last-child { border-bottom: none; }
    .stl-product-row:hover { transform: translateX(4px); }
    .stl-product-image {
      width: 84px;
      height: 100px;
      object-fit: cover;
      border-radius: 10px;
      background: #F8F8F8;
      flex-shrink: 0;
    }
    .stl-product-info { flex: 1; min-width: 0; }
    .stl-product-role {
      color: #C49A3C;
      text-transform: uppercase;
      letter-spacing: .12em;
      font-weight: 700;
      font-size: 10px;
      margin-bottom: 6px;
    }
    .stl-product-name {
      font-family: "Playfair Display", serif;
      font-size: 18px;
      color: #111110;
      margin-bottom: 6px;
    }
    .stl-product-price {
      color: #888780;
      font-size: 14px;
      margin-bottom: 8px;
    }
    .stl-shop-now {
      border: none;
      background: none;
      padding: 0;
      color: #111110;
      font-weight: 700;
      font-size: 12px;
      letter-spacing: .08em;
      text-transform: uppercase;
      cursor: pointer;
      border-bottom: 1px solid #111110;
      transition: color .25s ease, border-color .25s ease;
    }
    .stl-shop-now:hover {
      color: #C49A3C;
      border-color: #C49A3C;
    }
    .stl-empty {
      color: #888780;
      text-align: center;
      padding: 40px 20px;
    }
    .stl-footer {
      padding: 8px 32px 44px;
      text-align: center;
    }
    .stl-add-look {
      border: none;
      background: #111110;
      color: #fff;
      padding: 18px 56px;
      
      font-weight: 700;
      font-size: 14px;
      letter-spacing: .1em;
      text-transform: uppercase;
      cursor: pointer;
      transition: all .3s ease;
    }
    .stl-add-look:hover {
      background: #C49A3C;
    }
    .stl-footer-links {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 28px;
      margin-top: 22px;
      flex-wrap: wrap;
    }
    .stl-link-btn {
      border: none;
      background: none;
      color: #3D3D3B;
      font-weight: 600;
      font-size: 13px;
      letter-spacing: .06em;
      text-transform: uppercase;
      cursor: pointer;
      padding-bottom: 2px;
      border-bottom: 1px solid transparent;
      transition: all .25s ease;
    }
    .stl-link-btn:hover {
      color: #C49A3C;
      border-bottom-color: #C49A3C;
    }
    @media (max-width: 900px) {
      .stl-body { grid-template-columns: 1fr; gap: 32px; padding: 32px 20px; }
      .stl-image-wrap { min-height: clamp(280px, 78vw, 480px); }
      .stl-footer { padding: 8px 20px 36px; }
      .stl-footer-links { flex-direction: column; gap: 16px; }
    }
  `}</style>

  {/* Header */}
  <div className="stl-header">
    <div className="stl-eyebrow">Kit Collections</div>
    <h3 className="stl-title">Available Sets</h3>

    <div className="stl-collection-row">
      <span className="stl-collection-category">
        {selectedSet?.category_name || "Collection"}
      </span>
      <span className="stl-collection-name">
        {selectedSet?.name || "No Collection Selected"}
      </span>
      {selectedSet && (
        <span className="stl-collection-price">
          £{selectedSet?.total_price ?? selectedSet?.price ?? "0.00"}
        </span>
      )}
    </div>

    {sets.length > 1 && (
      <div className="stl-dots">
        {sets.map((set, idx) => (
          <button
            key={set.id}
            className={`stl-dot ${currentSetIndex === idx ? "active" : ""}`}
            onClick={() => setSelectedSet(set)}
            aria-label={`View ${set.name}`}
          />
        ))}
      </div>
    )}
  </div>
  
        <div className="stl-footer-links">
          <button
            className="stl-link-btn"
            onClick={() => {
              setSelectedSet(selectedSet);
              heroRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }}
          >
            View Full Collection
          </button>

          {sets.length > 1 && (
            <button
              className="stl-link-btn"
              onClick={() => goToSet(currentSetIndex + 1)}
            >
              Next Look →
            </button>
          )}
        </div>
  

  {selectedSet ? (
    <>
      <div className="stl-body">
        {/* Large set image */}
        <div className="stl-image-wrap">
          <img
            src={selectedSet?.image_url || "/images/product-placeholder.png"}
            alt={selectedSet?.name || "Kit set"}
            className="stl-image"
          />
          {sets.length > 1 && (
            <>
              <button
                className="stl-arrow prev"
                onClick={() => goToSet(currentSetIndex - 1)}
                aria-label="Previous look"
              >
                ←
              </button>
              <button
                className="stl-arrow next"
                onClick={() => goToSet(currentSetIndex + 1)}
                aria-label="Next look"
              >
                →
              </button>
            </>
          )}
        </div>

        {/* Product list */}
        <div className="stl-products">
          {kitLoading ? (
            <div className="stl-empty">Loading kit items...</div>
          ) : kitItems.length === 0 ? (
            <div className="stl-empty">
              No kit items available for this look yet.
            </div>
          ) : (
            kitItems.map((item) => (
              <div className="stl-product-row" key={item.id}>
                <img
                  src={item.image_url || "/images/product-placeholder.png"}
                  alt={item.name}
                  className="stl-product-image"
                />
                <div className="stl-product-info">
                  <div className="stl-product-role">{item.role}</div>
                  <div className="stl-product-name">{item.name}</div>
                  <div className="stl-product-price">
                    {item.placeholder || item.price == null
                      ? "Not Available Yet"
                      : `£${Number(item.price).toFixed(2)}`}
                  </div>
                  {!item.placeholder && (
                    <button
                      className="stl-shop-now"
                      onClick={() =>
                        item.slug && navigate("/product/" + item.slug)
                      }
                    >
                      Shop Now
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="stl-footer">
        <button className="stl-add-look" onClick={addLookToBag}>
          Add Look To Bag
        </button>
      </div>
    </>
  ) : (
    <div className="stl-empty" style={{ padding: "60px 32px" }}>
      No kit collections available.
    </div>
  )}
</div>
          {/* <aside
              className="luxe-card luxe-collection"
              style={{
                marginTop: 80,
                overflow: "hidden",
              }}
>
            <div
  style={{
    padding: "40px 32px",
    textAlign: "center",
    background: "#fff",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
  }}
>
  <div
    style={{
      color: "#C49A3C",
      textTransform: "uppercase",
      letterSpacing: ".15em",
      fontWeight: 700,
      fontSize: 12,
      marginBottom: 12,
    }}
  >
    Recommended Products
  </div>

  <h3
    style={{
      margin: 0,
      fontFamily: "Playfair Display, serif",
      fontSize: 38,
      fontWeight: 600,
      color: "#111110",
    }}
  >
    Curated Selection
  </h3>

  <p
    style={{
      marginTop: 16,
      color: "#888780",
      fontSize: 15,
      lineHeight: 1.7,
      maxWidth: 500,
      marginInline: "auto",
    }}
  >
    Explore top picks selected to complement your
    garden and match what you're growing.
  </p>

  <div
    style={{
      width: 70,
      height: 2,
      background: "#C49A3C",
      margin: "24px auto 0",
    }}
  />

  <div
    style={{
      marginTop: 18,
      color: "#888780",
      fontSize: 13,
      fontWeight: 600,
    }}
  >
    {aiQuery ? `AI Results (${total})` : `${total} Products`}
  </div>
</div>
  
            <div className="luxe-items">
              {!loading && !aiLoading && collection.length === 0 && (
                <div
                  style={{
                    width: "100%",
                    minHeight: 88,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#60737c",
                    padding: "12px 14px",
                  }}
                >
                  No products found. Use admin panel to add categories/products
                  or refine your query.
                </div>
              )}
              {collection.map((p, index) => {
                const thumb = productImages[p.id];
                return (
                  <button
                    key={p.id}
                    className={`luxe-item ${selectedProduct?.id === p.id ? "active" : ""}`}
                    onClick={() => {
                      setSelectedId(p.id);
                      setCurrentImage(0);
                    }}
                    style={{ border: "none" }}
                  >
                    {thumb ? (
                      <img
                        src={thumb}
                        alt={p.name}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                     <div
  className="luxe-skeleton"
  style={{
    width: "100%",
    height: 260,
    borderRadius: 14,
  }}
/>
                    )}
                    <div style={{ textAlign: "left" }}>
                      <div
                        style={{
                          color: "#60737c",
                          fontSize: 11,
                          textTransform: "uppercase",
                          letterSpacing: ".08em",
                        }}
                      >
                        {p.category_name || "Uncategorized"}
                      </div>
                     <div
                      style={{
                        color: "#111110",
                        fontWeight: 600,
                        fontSize: 15,
                        lineHeight: 1.5,
                        marginTop: 4,
                      }}
                    >
                        {p.name}
                      </div>
                      <div style={{ color: "#7f8f96", fontSize: 12 }}>
                        {p.brand_name || "Brand"}
                      </div>
                    </div>
                    <div
                      style={{
                        color: "#111110",
                        fontWeight: 700,
                        fontSize: 16,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {money(p.sale_price || p.price)}
                    </div>
                  </button>
                );
              })}
            </div>
          </aside> */}
        </div>

        {/* {!aiQuery && totalPages > 1 && (
          <div
            style={{
              marginTop: 10,
              display: "flex",
              justifyContent: "center",
              gap: 6,
              flexWrap: "wrap",
            }}
          >
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                onClick={() => setPageNum(n)}
                className="luxe-icon-btn"
                style={{
                  background:
                    n === page
                      ? "linear-gradient(180deg, #d8b66a, #b58a2b)"
                      : "#f6f8f9",
                  color: n === page ? "#fff" : "#21414d",
                }}
              >
                {n}
              </button>
            ))}
          </div>
        )} */}

        {/* <div className={`luxe-chat ${chatOpen ? "open" : ""}`}>
          <button
            className="luxe-chat-toggle"
            onClick={() => setChatOpen((v) => !v)}
          >
            <span>AI Product Finder</span>
            <span>{chatOpen ? "▼" : "▲"}</span>
          </button>
          <div className="luxe-chat-panel">
            <p style={{ margin: "0 0 10px", color: "#60737c", fontSize: 13 }}>
              Describe your style and our personal stylist
              will curate products from the collection.
            </p>
            <div className="luxe-chat-chips">
              {(collection || []).slice(0, 3).map((p) => (
                <button key={p.id} onClick={() => runAiSearch(p.name)}>
                  {p.name}
                </button>
              ))}
            </div>
            <div className="luxe-chat-input-row">
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") runAiSearch(searchInput);
                }}
                placeholder="e.g. performance, swimwear, lightweight"
              />
              <button
                className="luxe-btn"
                onClick={() => runAiSearch(searchInput)}
              >
                Search
              </button>
            </div>
            {aiQuery && (
              <p style={{ margin: "10px 0 0", color: "#8f6a1d", fontSize: 12 }}>
                AI query: {aiQuery}
              </p>
            )}
          </div>
        </div> */}
      </div>
  );
}
